// Strudel source string → block tree.
//
// Uses the Lezer JS parser shipped with `@codemirror/lang-javascript`
// (no extra parser dependency). Walks the syntax tree and pattern-
// matches shapes against the block vocabulary. Anything unrecognised
// becomes a structured error pointing at the offending source range.

import { javascriptLanguage } from '@codemirror/lang-javascript';
import type { SyntaxNode } from '@lezer/common';
import { patternFromString } from './mini';
import {
  isConditionalKind,
  newBlockId,
  type ArrangeBlock,
  type ConditionalKind,
  type Effect,
  type EffectArg,
  type EffectKind,
  type Expression,
  type ModulatorSource,
  type Numeric,
  type ParseError,
  type ParseResult,
  type SimpleEffectKind,
  type Statement,
  type VoiceBlock,
} from './types';

// ─── Public API ─────────────────────────────────────────────────────

export function parseCode(code: string): ParseResult {
  const tree = javascriptLanguage.parser.parse(code);
  const ctx: Ctx = { code };

  const statements: Statement[] = [];
  let leadingComment: string | undefined;
  // Source range of the leading-comment accumulator, so we can promote
  // it to a standalone Comment statement when a blank line separates
  // it from the next statement.
  let leadingFrom: number | undefined;
  let leadingTo: number | undefined;

  const promoteToFloating = () => {
    if (leadingComment === undefined || leadingFrom === undefined || leadingTo === undefined) {
      return;
    }
    statements.push({
      id: newBlockId(),
      kind: 'comment',
      content: leadingComment,
      sourceRange: { from: leadingFrom, to: leadingTo },
    });
    leadingComment = undefined;
    leadingFrom = undefined;
    leadingTo = undefined;
  };

  let node: SyntaxNode | null = tree.topNode.firstChild;
  while (node) {
    if (isCommentNode(node.name)) {
      // Trailing comment? Only if this comment sits on the same source
      // line as the most recently parsed statement — i.e. nothing but
      // horizontal whitespace between them.
      const lastStmt = statements[statements.length - 1];
      const isTrailing =
        lastStmt !== undefined &&
        lastStmt.sourceRange !== undefined &&
        !code.slice(lastStmt.sourceRange.to, node.from).includes('\n');

      if (isTrailing && lastStmt && lastStmt.kind !== 'comment') {
        lastStmt.trailingComment = lastStmt.trailingComment
          ? lastStmt.trailingComment + ' ' + slice(node, ctx)
          : slice(node, ctx);
      } else {
        // If we already have a run of leading comments and this new
        // comment is separated from the previous by a blank line,
        // the previous run was floating — flush it first.
        if (leadingTo !== undefined) {
          const gap = code.slice(leadingTo, node.from);
          if ((gap.match(/\n/g) || []).length >= 2) {
            promoteToFloating();
          }
        }
        if (leadingFrom === undefined) leadingFrom = node.from;
        leadingTo = node.to;
        leadingComment = leadingComment
          ? leadingComment + '\n' + slice(node, ctx)
          : slice(node, ctx);
      }
    } else {
      // Non-comment statement. If a leading comment exists and is
      // separated from this statement by a blank line, that comment
      // is floating, not leading.
      if (leadingComment !== undefined && leadingTo !== undefined) {
        const gap = code.slice(leadingTo, node.from);
        if ((gap.match(/\n/g) || []).length >= 2) {
          promoteToFloating();
        }
      }
      const stmtResult = parseStatement(node, ctx, leadingComment);
      if (!stmtResult.ok) return stmtResult;
      stmtResult.statement.sourceRange = { from: node.from, to: node.to };
      statements.push(stmtResult.statement);
      leadingComment = undefined;
      leadingFrom = undefined;
      leadingTo = undefined;
    }
    node = node.nextSibling;
  }

  // End-of-file comments become a floating Comment statement.
  if (leadingComment !== undefined) {
    promoteToFloating();
  }

  return { ok: true, song: { statements } };
}

// ─── Context & helpers ──────────────────────────────────────────────

type Ctx = { code: string };
type Ok<T> = { ok: true } & T;

function fail(reason: string, node: SyntaxNode): ParseError {
  return { ok: false, reason, location: { from: node.from, to: node.to } };
}

function slice(node: SyntaxNode, ctx: Ctx): string {
  return ctx.code.slice(node.from, node.to);
}

function isCommentNode(name: string): boolean {
  return name === 'LineComment' || name === 'BlockComment';
}

// Lezer JS wraps top-level expressions in ExpressionStatement, whose only
// non-trivia child is the expression itself. Some node names include the
// trailing `;` as a separate node; we skip over non-significant siblings.
function significantChild(node: SyntaxNode): SyntaxNode | null {
  let c: SyntaxNode | null = node.firstChild;
  while (c && isTrivia(c.name)) c = c.nextSibling;
  return c;
}

function isTrivia(name: string): boolean {
  return TRIVIA.has(name);
}

// Lezer's JS grammar labels both literal tokens (`;`, `,`, `(`, `=`, …)
// and named token nodes (`Equals`, `Dot`, `ParenL`, …) depending on
// context. Include both forms.
const TRIVIA = new Set<string>([
  // Literal forms
  ';',
  ',',
  '(',
  ')',
  '[',
  ']',
  '{',
  '}',
  '=',
  '.',
  // Named forms
  'Equals',
  'Dot',
  'Comma',
  'Semicolon',
  'ParenL',
  'ParenR',
  'BracketL',
  'BracketR',
  'BraceL',
  'BraceR',
  // Declaration keywords
  'const',
  'let',
  'var',
]);

// ─── Statements ─────────────────────────────────────────────────────

function parseStatement(
  node: SyntaxNode,
  ctx: Ctx,
  leadingComment: string | undefined,
): Ok<{ statement: Statement }> | ParseError {
  if (node.name === 'VariableDeclaration') {
    return parseVariableDeclaration(node, ctx, leadingComment);
  }
  if (node.name === 'ExpressionStatement') {
    return parseExpressionStatement(node, ctx, leadingComment);
  }
  return fail(`Unsupported top-level node \`${node.name}\``, node);
}

function parseVariableDeclaration(
  node: SyntaxNode,
  ctx: Ctx,
  leadingComment: string | undefined,
): Ok<{ statement: Statement }> | ParseError {
  // Lezer shape: VariableDeclaration > VariableDeclaration/VariableDefinition > CallExpression
  // But practically: we look for the VariableDefinition child and its initializer sibling.
  const declarator = significantChild(node);
  if (!declarator) return fail('Empty variable declaration', node);

  // The VariableDefinition holds the name; the initializer follows as the
  // next significant sibling in the declaration node.
  let nameNode: SyntaxNode | null = null;
  let initNode: SyntaxNode | null = null;

  let c: SyntaxNode | null = node.firstChild;
  while (c) {
    if (c.name === 'VariableDefinition') nameNode = c;
    else if (!isTrivia(c.name) && c !== nameNode && !isCommentNode(c.name)) {
      // First expression-ish sibling after the name is the initializer.
      // `const` itself is already classified as trivia.
      if (nameNode) {
        initNode = c;
        break;
      }
    }
    c = c.nextSibling;
  }

  if (!nameNode) return fail('Missing binding name in const', node);
  if (!initNode) return fail('Missing initializer in const', node);

  const name = slice(nameNode, ctx);

  // Check for arrow function shape — preserved verbatim as customFunction.
  if (initNode.name === 'ArrowFunction' || initNode.name === 'FunctionExpression') {
    const expr: Expression = {
      id: newBlockId(),
      kind: 'customFunction',
      source: slice(initNode, ctx),
    };
    return {
      ok: true,
      statement: {
        id: newBlockId(),
        kind: 'const',
        name,
        expr,
        leadingComment,
      },
    };
  }

  const exprResult = parseExpression(initNode, ctx);
  if (!exprResult.ok) return exprResult;

  return {
    ok: true,
    statement: {
      id: newBlockId(),
      kind: 'const',
      name,
      expr: exprResult.expr,
      leadingComment,
    },
  };
}

function parseExpressionStatement(
  node: SyntaxNode,
  ctx: Ctx,
  leadingComment: string | undefined,
): Ok<{ statement: Statement }> | ParseError {
  const inner = significantChild(node);
  if (!inner) return fail('Empty expression statement', node);

  // Recognise setcpm / setcps / samples as distinct statement shapes
  // rather than regular expressions. Keeps the block tree flat at top
  // level and gives each its own card UI.
  if (inner.name === 'CallExpression') {
    const callee = inner.firstChild;
    if (callee && callee.name === 'VariableName') {
      const calleeName = slice(callee, ctx);
      if (calleeName === 'setcpm' || calleeName === 'setcps') {
        const argList = inner.getChild('ArgList');
        if (!argList) return fail(`${calleeName} expects an argument`, inner);
        const arg = firstArg(argList);
        if (!arg) return fail(`${calleeName} expects an argument`, inner);
        const num = parseNumeric(arg, ctx);
        if (!num.ok) return num;
        return {
          ok: true,
          statement: {
            id: newBlockId(),
            kind: calleeName,
            value: num.value,
            leadingComment,
          },
        };
      }
      if (calleeName === 'samples') {
        const argList = inner.getChild('ArgList');
        if (!argList) return fail('samples() expects an argument', inner);
        const arg = firstArg(argList);
        if (!arg) return fail('samples() expects an argument', inner);
        if (arg.name !== 'String') {
          return fail(
            'samples() block-editor support is limited to a single string URL (e.g. samples("github:user/repo"))',
            arg,
          );
        }
        return {
          ok: true,
          statement: {
            id: newBlockId(),
            kind: 'samples',
            source: unwrapString(slice(arg, ctx)),
            leadingComment,
          },
        };
      }
    }
  }

  const exprResult = parseExpression(inner, ctx);
  if (!exprResult.ok) return exprResult;
  return {
    ok: true,
    statement: {
      id: newBlockId(),
      kind: 'expression',
      expr: exprResult.expr,
      leadingComment,
    },
  };
}

// ─── Expressions ────────────────────────────────────────────────────

type ExprOk = { ok: true; expr: Expression };

function parseExpression(node: SyntaxNode, ctx: Ctx): ExprOk | ParseError {
  const result = parseExpressionInner(node, ctx);
  if (result.ok) {
    result.expr.sourceRange = { from: node.from, to: node.to };
  }
  return result;
}

function parseExpressionInner(node: SyntaxNode, ctx: Ctx): ExprOk | ParseError {
  if (node.name === 'VariableName') {
    return {
      ok: true,
      expr: { id: newBlockId(), kind: 'ref', name: slice(node, ctx) },
    };
  }
  if (node.name === 'CallExpression') {
    return parseCallExpression(node, ctx);
  }
  if (node.name === 'ArrowFunction' || node.name === 'FunctionExpression') {
    return {
      ok: true,
      expr: {
        id: newBlockId(),
        kind: 'customFunction',
        source: slice(node, ctx),
      },
    };
  }
  return fail(`Unsupported expression \`${node.name}\``, node);
}

function parseCallExpression(node: SyntaxNode, ctx: Ctx): ExprOk | ParseError {
  // Unwrap the method chain: walk leftward collecting effects, find the base call.
  const effects: Effect[] = [];
  let current: SyntaxNode = node;

  while (true) {
    const callee = current.firstChild;
    if (!callee) return fail('Malformed call expression', current);

    if (callee.name === 'MemberExpression') {
      // `.method` — current call is an effect on whatever the member targets.
      const effectResult = parseEffectCall(current, callee, ctx);
      if (!effectResult.ok) return effectResult;
      effects.unshift(effectResult.effect);
      const target = callee.firstChild;
      if (!target) return fail('Missing method target', callee);
      if (target.name === 'CallExpression') {
        current = target;
        continue;
      }
      if (target.name === 'VariableName') {
        // Chain originates on a bare identifier — `bass.lpf(250)` style.
        // Treat as a reference carrying a method chain.
        return {
          ok: true,
          expr: {
            id: newBlockId(),
            kind: 'ref',
            name: slice(target, ctx),
            effects,
          },
        };
      }
      return fail(
        `Method chain must originate in a call expression or identifier (got \`${target.name}\`)`,
        target,
      );
    }

    if (callee.name === 'VariableName') {
      const name = slice(callee, ctx);
      const argList = current.getChild('ArgList');
      if (!argList) return fail(`Call to \`${name}\` missing argument list`, current);
      return parseBaseCall(name, current, argList, effects, ctx);
    }

    return fail(`Unsupported callee \`${callee.name}\``, callee);
  }
}

function parseBaseCall(
  name: string,
  callNode: SyntaxNode,
  argList: SyntaxNode,
  effects: Effect[],
  ctx: Ctx,
): ExprOk | ParseError {
  switch (name) {
    case 's':
    case 'note': {
      const arg = firstArg(argList);
      if (!arg) return fail(`${name}(…) expects a pattern argument`, callNode);
      if (arg.name !== 'String') {
        return fail(`${name}(…) expects a string literal`, arg);
      }
      const pattern = patternFromString(unwrapString(slice(arg, ctx)));
      const voice: VoiceBlock = {
        id: newBlockId(),
        kind: name === 's' ? 'voice.s' : 'voice.note',
        pattern,
        effects,
      };
      return { ok: true, expr: voice };
    }
    case 'stack': {
      if (effects.length > 0) {
        return fail('Method chain on stack() not yet supported', callNode);
      }
      const children: Expression[] = [];
      const args = iterArgs(argList);
      for (const arg of args) {
        const res = parseExpression(arg, ctx);
        if (!res.ok) return res;
        children.push(res.expr);
      }
      return {
        ok: true,
        expr: { id: newBlockId(), kind: 'stack', children },
      };
    }
    case 'arrange': {
      if (effects.length > 0) {
        return fail('Method chain on arrange() not yet supported', callNode);
      }
      const entries: ArrangeBlock['entries'] = [];
      const args = iterArgs(argList);
      for (const arg of args) {
        if (arg.name !== 'ArrayExpression') {
          return fail(`arrange expects [cycles, section] tuples`, arg);
        }
        const inner = arrayInner(arg);
        if (inner.length !== 2) {
          return fail(`arrange tuple must have exactly 2 elements`, arg);
        }
        const cycRes = parseNumeric(inner[0]!, ctx);
        if (!cycRes.ok) return cycRes;
        const secRes = parseExpression(inner[1]!, ctx);
        if (!secRes.ok) return secRes;
        entries.push({
          id: newBlockId(),
          cycles: cycRes.value,
          section: secRes.expr,
        });
      }
      return {
        ok: true,
        expr: { id: newBlockId(), kind: 'arrange', entries },
      };
    }
    default:
      return fail(`Unsupported top-level call \`${name}\``, callNode);
  }
}

function parseEffectCall(
  callNode: SyntaxNode,
  member: SyntaxNode,
  ctx: Ctx,
): { ok: true; effect: Effect } | ParseError {
  const nameNode = member.getChild('PropertyName');
  if (!nameNode) return fail('Missing method name', member);
  const name = slice(nameNode, ctx);
  if (isConditionalKind(name)) {
    return parseConditionalEffect(name, callNode, ctx);
  }
  if (!isSimpleEffectKind(name)) {
    return fail(`Unsupported method \`.${name}\``, nameNode);
  }
  const argList = callNode.getChild('ArgList');
  const args: EffectArg[] = [];
  if (argList) {
    for (const argNode of iterArgs(argList)) {
      const argRes = parseEffectArg(argNode, name, ctx);
      if (!argRes.ok) return argRes;
      args.push(argRes.arg);
    }
  }
  return {
    ok: true,
    effect: {
      id: newBlockId(),
      kind: name,
      args,
      sourceRange: { from: callNode.from, to: callNode.to },
    },
  };
}

// `.every(4, x => x.fast(2))` → ConditionalEffect with condArgs=[4],
// body=[fast(2)]. The canonical shape is an arrow function whose body
// is a call chain on the sole parameter; anything else (multi-arg
// function, non-chain body, nested blocks) is rejected so the user
// sees a clean error instead of the conditional silently swallowing
// opaque code.
function parseConditionalEffect(
  name: ConditionalKind,
  callNode: SyntaxNode,
  ctx: Ctx,
): { ok: true; effect: Effect } | ParseError {
  const argList = callNode.getChild('ArgList');
  if (!argList) return fail(`.${name}(…) missing argument list`, callNode);
  const args = Array.from(iterArgs(argList));
  if (args.length === 0) {
    return fail(`.${name}(…) needs a transform function`, callNode);
  }
  const bodyNode = args[args.length - 1]!;
  const condNodes = args.slice(0, -1);

  const condArgs: EffectArg[] = [];
  for (const node of condNodes) {
    const num = parseNumeric(node, ctx);
    if (!num.ok) return num;
    condArgs.push({ kind: 'numeric', value: num.value });
  }

  const body = parseConditionalBody(bodyNode, ctx);
  if (!body.ok) return body;

  return {
    ok: true,
    effect: {
      id: newBlockId(),
      kind: name,
      condArgs,
      body: body.effects,
      sourceRange: { from: callNode.from, to: callNode.to },
    },
  };
}

function parseConditionalBody(
  node: SyntaxNode,
  ctx: Ctx,
): { ok: true; effects: Effect[] } | ParseError {
  if (node.name !== 'ArrowFunction') {
    return fail(
      `Conditional body must be an arrow function like \`x => x.method(…)\` (got ${node.name})`,
      node,
    );
  }
  const { paramName, body } = unpackArrowFunction(node, ctx);
  if (!paramName) return fail('Arrow function missing a single parameter', node);
  if (!body) return fail('Arrow function missing a body expression', node);

  // A bare `x => x` body is meaningful (identity) — the conditional
  // has no effects and thus does nothing. Allow it.
  if (body.name === 'VariableName' && slice(body, ctx) === paramName) {
    return { ok: true, effects: [] };
  }

  if (body.name !== 'CallExpression') {
    return fail(
      `Arrow function body must be a method chain on ${JSON.stringify(paramName)}`,
      body,
    );
  }

  const exprRes = parseExpression(body, ctx);
  if (!exprRes.ok) return exprRes;
  const expr = exprRes.expr;
  if (expr.kind !== 'ref' || expr.name !== paramName) {
    return fail(
      `Arrow function body must chain methods on ${JSON.stringify(paramName)}`,
      body,
    );
  }
  return { ok: true, effects: expr.effects ?? [] };
}

// Walk an ArrowFunction node and pick out its single-parameter name
// and body expression. Lezer represents the shape loosely — the param
// may sit inside a ParamList or as a bare VariableDefinition; the body
// is the last significant child after the `=>` token.
function unpackArrowFunction(
  node: SyntaxNode,
  ctx: Ctx,
): { paramName: string | null; body: SyntaxNode | null } {
  let paramName: string | null = null;
  let body: SyntaxNode | null = null;

  let c: SyntaxNode | null = node.firstChild;
  while (c) {
    if (c.name === 'ParamList') {
      const def = c.getChild('VariableDefinition');
      if (def) paramName = slice(def, ctx);
    } else if (c.name === 'VariableDefinition' && !paramName) {
      paramName = slice(c, ctx);
    } else if (!isTrivia(c.name) && !isCommentNode(c.name)) {
      // The final non-trivia, non-definition sibling is the body.
      body = c;
    }
    c = c.nextSibling;
  }
  return { paramName, body };
}

function parseEffectArg(
  node: SyntaxNode,
  kind: SimpleEffectKind,
  ctx: Ctx,
): { ok: true; arg: EffectArg } | ParseError {
  // .struct("pattern") and .mask("pattern") take a mini-notation pattern.
  if (kind === 'struct' || kind === 'mask') {
    if (node.name !== 'String') return fail(`.${kind}(…) expects a string`, node);
    const source = unwrapString(slice(node, ctx));
    return { ok: true, arg: { kind: 'pattern', value: patternFromString(source) } };
  }
  // Effects that take an identifier-ish string literal (synth name,
  // scale/chord key, arp preset). Strudel also accepts these as
  // patterns sometimes, but the corpus/examples use plain strings.
  if (STRING_ARG_EFFECTS[kind]) {
    if (node.name !== 'String') return fail(`.${kind}(…) expects a string`, node);
    return { ok: true, arg: { kind: 'string', value: unwrapString(slice(node, ctx)) } };
  }
  // Otherwise numeric.
  const num = parseNumeric(node, ctx);
  if (!num.ok) return num;
  return { ok: true, arg: { kind: 'numeric', value: num.value } };
}

// Effects whose args are string literals (not numerics, not patterns).
// Declared as a Partial record so a missing kind is clearly false.
const STRING_ARG_EFFECTS: Partial<Record<SimpleEffectKind, true>> = {
  s: true,
  scale: true,
  chord: true,
  arp: true,
  vowel: true,
  adsr: true,
  ad: true,
};

// ─── Numerics ───────────────────────────────────────────────────────

type NumOk = { ok: true; value: Numeric };

function parseNumeric(node: SyntaxNode, ctx: Ctx): NumOk | ParseError {
  if (node.name === 'Number') {
    return { ok: true, value: { kind: 'num', value: Number(slice(node, ctx)) } };
  }
  if (node.name === 'ParenthesizedExpression') {
    // Unwrap the parens and parse whatever's inside. Preserves the
    // structured binary/modulator/ref shape for `setcpm((120 + 10) / 4)`
    // that would otherwise fall through to raw preservation.
    const inner = significantChild(node);
    if (!inner) return fail('Empty parentheses', node);
    return parseNumeric(inner, ctx);
  }
  if (node.name === 'UnaryExpression') {
    // `-3` etc. Recurse into operand, negate if unary minus.
    const opNode = node.firstChild;
    const operand = opNode?.nextSibling;
    if (opNode && operand && slice(opNode, ctx) === '-') {
      const inner = parseNumeric(operand, ctx);
      if (!inner.ok) return inner;
      if (inner.value.kind === 'num') {
        return { ok: true, value: { kind: 'num', value: -inner.value.value } };
      }
    }
    return {
      ok: true,
      value: { kind: 'raw', source: slice(node, ctx) },
    };
  }
  if (node.name === 'BinaryExpression') {
    const lhs = node.firstChild;
    const opNode = lhs?.nextSibling;
    const rhs = opNode?.nextSibling;
    if (!lhs || !opNode || !rhs) {
      return fail('Malformed binary expression', node);
    }
    const op = slice(opNode, ctx) as '+' | '-' | '*' | '/';
    if (op !== '+' && op !== '-' && op !== '*' && op !== '/') {
      return {
        ok: true,
        value: { kind: 'raw', source: slice(node, ctx) },
      };
    }
    const lhsRes = parseNumeric(lhs, ctx);
    if (!lhsRes.ok) return lhsRes;
    const rhsRes = parseNumeric(rhs, ctx);
    if (!rhsRes.ok) return rhsRes;
    return {
      ok: true,
      value: { kind: 'binary', op, lhs: lhsRes.value, rhs: rhsRes.value },
    };
  }
  if (node.name === 'VariableName') {
    return { ok: true, value: { kind: 'ref', name: slice(node, ctx) } };
  }
  if (node.name === 'CallExpression') {
    // Modulator chains: sine.range(a, b).slow(n), perlin.range(a, b).slow(n), etc.
    const mod = parseModulator(node, ctx);
    if (mod) return mod;
    // Anything else — preserve verbatim as raw.
    return { ok: true, value: { kind: 'raw', source: slice(node, ctx) } };
  }
  // Fallback for expressions we don't model: preserve verbatim.
  return { ok: true, value: { kind: 'raw', source: slice(node, ctx) } };
}

function parseModulator(node: SyntaxNode, ctx: Ctx): NumOk | null {
  // Work leftward to find the source identifier and accumulate .range/.slow/.fast calls.
  let range: [Numeric, Numeric] | undefined;
  let slow: Numeric | undefined;
  let fast: Numeric | undefined;
  let modArgs: Numeric[] | undefined;
  let current: SyntaxNode = node;
  while (current.name === 'CallExpression') {
    const callee = current.firstChild;
    if (!callee) return null;
    // Innermost-call form: `knob(21)` or `knob(21, 1)`. Callee is a
    // VariableName, not a MemberExpression — capture the source's
    // positional args and break out of the loop.
    if (callee.name === 'VariableName') {
      const argList = current.getChild('ArgList');
      if (!argList) return null;
      const argNodes = Array.from(iterArgs(argList));
      const parsed: Numeric[] = [];
      for (const a of argNodes) {
        const n = parseNumeric(a, ctx);
        if (!n.ok) return null;
        parsed.push(n.value);
      }
      modArgs = parsed;
      current = callee;
      break;
    }
    if (callee.name !== 'MemberExpression') return null;
    const target = callee.firstChild;
    const propNode = callee.getChild('PropertyName');
    if (!target || !propNode) return null;
    const prop = slice(propNode, ctx);
    const argList = current.getChild('ArgList');
    if (!argList) return null;
    const args = Array.from(iterArgs(argList));
    if (prop === 'range' && args.length === 2) {
      const lo = parseNumeric(args[0]!, ctx);
      const hi = parseNumeric(args[1]!, ctx);
      if (!lo.ok || !hi.ok) return null;
      range = [lo.value, hi.value];
    } else if (prop === 'slow' && args.length === 1) {
      const n = parseNumeric(args[0]!, ctx);
      if (!n.ok) return null;
      slow = n.value;
    } else if (prop === 'fast' && args.length === 1) {
      const n = parseNumeric(args[0]!, ctx);
      if (!n.ok) return null;
      fast = n.value;
    } else {
      return null;
    }
    current = target;
  }
  if (current.name !== 'VariableName') return null;
  const sourceName = slice(current, ctx);
  if (!isModulatorSource(sourceName)) return null;
  return {
    ok: true,
    value: {
      kind: 'modulator',
      source: sourceName,
      args: modArgs,
      range,
      slow,
      fast,
    },
  };
}

// ─── Token utilities ────────────────────────────────────────────────

function firstArg(argList: SyntaxNode): SyntaxNode | null {
  let c: SyntaxNode | null = argList.firstChild;
  while (c && isTrivia(c.name)) c = c.nextSibling;
  return c;
}

function* iterArgs(argList: SyntaxNode): Iterable<SyntaxNode> {
  let c: SyntaxNode | null = argList.firstChild;
  while (c) {
    if (!isTrivia(c.name)) yield c;
    c = c.nextSibling;
  }
}

function arrayInner(array: SyntaxNode): SyntaxNode[] {
  const out: SyntaxNode[] = [];
  let c: SyntaxNode | null = array.firstChild;
  while (c) {
    if (!isTrivia(c.name)) out.push(c);
    c = c.nextSibling;
  }
  return out;
}

function unwrapString(raw: string): string {
  // Lezer's String node includes the surrounding quotes.
  if (raw.length < 2) return raw;
  const quote = raw[0];
  if (quote === '"' || quote === "'" || quote === '`') {
    return raw.slice(1, -1);
  }
  return raw;
}

// ─── Vocab tables ───────────────────────────────────────────────────
//
// Expressed as Record<Union, true> so adding a new member to the
// EffectKind / ModulatorSource types flags a compile error here until
// the parser's lookup table catches up. Protects against silent
// rejection when the type expands.

const SIMPLE_EFFECT_KIND_LOOKUP: Record<SimpleEffectKind, true> = {
  gain: true,
  speed: true,
  pan: true,
  lpf: true,
  lpq: true,
  hpf: true,
  hpq: true,
  s: true,
  attack: true,
  release: true,
  delay: true,
  delaytime: true,
  delayfb: true,
  struct: true,
  slow: true,
  fast: true,
  rev: true,
  palindrome: true,
  ply: true,
  stutter: true,
  iter: true,
  iterBack: true,
  early: true,
  late: true,
  linger: true,
  zoom: true,
  compress: true,
  scale: true,
  chord: true,
  voicing: true,
  arp: true,
  transpose: true,
  strans: true,
  bjork: true,
  add: true,
  sub: true,
  mul: true,
  bpf: true,
  bpq: true,
  djf: true,
  crush: true,
  coarse: true,
  distort: true,
  shape: true,
  squiz: true,
  comb: true,
  phaser: true,
  phaserdepth: true,
  phaserfreq: true,
  phaserrate: true,
  decay: true,
  sustain: true,
  velocity: true,
  amp: true,
  clip: true,
  accelerate: true,
  dough: true,
  n: true,
  orbit: true,
  octave: true,
  cut: true,
  begin: true,
  end: true,
  unison: true,
  detune: true,
  chorus: true,
  noise: true,
  room: true,
  roomsize: true,
  predelay: true,
  size: true,
  hcut: true,
  lcut: true,
  dry: true,
  vowel: true,
  adsr: true,
  ad: true,
  loop: true,
  loopBegin: true,
  loopEnd: true,
  loopFirst: true,
  chop: true,
  striate: true,
  striateBy: true,
  fit: true,
  fitslice: true,
  slice: true,
  echo: true,
  swing: true,
  swingBy: true,
  degrade: true,
  degradeBy: true,
  undegrade: true,
  undegradeBy: true,
  loopAt: true,
  pace: true,
  repeatCycles: true,
  mask: true,
};

function isSimpleEffectKind(name: string): name is SimpleEffectKind {
  return Object.prototype.hasOwnProperty.call(SIMPLE_EFFECT_KIND_LOOKUP, name);
}

const MODULATOR_SOURCE_LOOKUP: Record<ModulatorSource, true> = {
  sine: true,
  cosine: true,
  square: true,
  saw: true,
  tri: true,
  rand: true,
  irand: true,
  perlin: true,
  knob: true,
};

function isModulatorSource(name: string): name is ModulatorSource {
  return Object.prototype.hasOwnProperty.call(MODULATOR_SOURCE_LOOKUP, name);
}
